import { FormEvent, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { loginTenant, ApiError, isPlatformAdmin, postLoginRedirect } from "../lib/api.js";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromOverride = (location.state as { from?: string } | null)?.from;

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [workspace, setWorkspace] = useState("");
  const [needsWorkspace, setNeedsWorkspace] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const session = await loginTenant({
        username: username.trim(),
        password,
        workspace: needsWorkspace ? workspace.trim() : undefined
      });
      if (fromOverride) {
        const allowsPlatformAdmin = fromOverride.startsWith("/admin");
        const allowsTenant = fromOverride.startsWith("/app");
        if (
          (isPlatformAdmin(session) && allowsPlatformAdmin) ||
          (!isPlatformAdmin(session) && allowsTenant)
        ) {
          navigate(fromOverride, { replace: true });
          return;
        }
      }
      navigate(postLoginRedirect(session), { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === "workspace_required") {
        setNeedsWorkspace(true);
        setError("This username exists in multiple workspaces. Enter your workspace slug to continue.");
      } else {
        const msg = err instanceof ApiError
          ? `${err.message}${err.code ? ` (${err.code})` : ""}`
          : err instanceof Error ? err.message : "Sign-in failed";
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-bar">
        <Link to="/" className="brand">
          <img src="/brand-logo-light.svg" alt="TallyStream" />
        </Link>
        <Link to="/signup" className="btn">Create a workspace</Link>
      </div>

      <div className="auth-body">
        <form className="auth-card" onSubmit={onSubmit}>
          <h1>Sign in to TallyStream</h1>
          <p className="sub">
            Use your TallyStream account. DHIS2 instances are selected after sign-in.
          </p>

          <div className="field">
            <label htmlFor="username">Username or email</label>
            <input
              id="username"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="your username or email"
              required
              autoFocus
            />
          </div>

          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {needsWorkspace && (
            <div className="field">
              <label htmlFor="workspace">Workspace slug</label>
              <input
                id="workspace"
                type="text"
                autoComplete="organization"
                value={workspace}
                onChange={(e) => setWorkspace(e.target.value)}
                placeholder="e.g. acme-health"
                required
              />
              <small className="muted">Only needed when your username exists in multiple workspaces.</small>
            </div>
          )}

          {error && <div className="alert alert-error">{error}</div>}

          <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
            {loading ? "Signing in…" : "Sign in"}
          </button>

          <p className="muted" style={{ fontSize: 13, textAlign: "center", marginTop: 16 }}>
            New to TallyStream? <Link to="/signup">Create a workspace</Link>
          </p>
          <p className="muted" style={{ fontSize: 12, textAlign: "center" }}>
            <Link to="/">← Back to overview</Link>
          </p>
        </form>
      </div>

      <div className="auth-foot">
        <span>TallyStream · VitalPlatforms</span>
        <span>tallystream.vitalplatforms.com</span>
      </div>
    </div>
  );
}
